/**
 * Socket.io Context
 * Manages WebSocket connection for real-time updates
 *
 * Uses HttpOnly cookies for authentication (same as AuthContext)
 * Provides real-time messaging capabilities across components
 */
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { io } from "socket.io-client";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "./AuthContext";
import { getEmbedToken } from "@/lib/embedSession";
import { announceSessionReplaced, isSessionReplacedBody } from "@/lib/sessionReplaced";

const SocketContext = createContext(null);

// Use relative path in development (Vite proxy), absolute in production
// Use relative path to force same-origin (proxy)
const SOCKET_URL = "/";

/**
 * Opções do cliente Socket.io.
 *
 * Exportado de propósito: o incidente de 2026-08-05 foi amplificado porque a
 * frota inteira voltava dentro de ~600ms depois de um restart da API
 * (`reconnectionDelay 1000 × randomizationFactor 0.3 × 2`), esgotando as
 * conexões do Postgres. O socket.io sorteia o delay em
 * `[delay − delay×fator, delay + delay×fator]`, ou seja uma janela de
 * `delay × fator × 2`; ela precisa ser ≥ 3000ms para diluir a volta.
 *
 * `reconnectionDelayMax` e `reconnectionAttempts` seguem inalterados — só a
 * primeira tentativa é espalhada, o comportamento em regime é o mesmo.
 */
export const SOCKET_IO_OPTIONS = {
  withCredentials: true, // Send HttpOnly cookies
  // WebSocket first, falls back to polling if handshake fails (e.g. 502 during deploy)
  transports: ["websocket", "polling"],
  upgrade: true,
  reconnection: true,
  reconnectionAttempts: Infinity,
  reconnectionDelay: 2000,
  reconnectionDelayMax: 10000, // 10s max — less time offline
  randomizationFactor: 0.8, // janela = 2000 × 0.8 × 2 = 3200ms
  timeout: 15000, // Detect failure faster
};

/**
 * Handshake `auth` payload (EMB-09).
 *
 * Function form (not a static object) on purpose: socket.io-client invokes
 * this on the INITIAL connect and again on EVERY automatic reconnect, so a
 * token renewed mid-session (EmbedContext, T12) reaches the next reconnect
 * without recreating the socket. `withCredentials` above still carries the
 * HttpOnly cookie for the first-party app — this only ADDS a token when the
 * embed shell has one; the normal app sends `{}`, identical to today
 * (the server already reads `handshake.auth.token` only when present —
 * apps/api/services/socketService.js:660, no server change).
 */
export function buildSocketAuth(cb) {
  const token = getEmbedToken();
  cb(token ? { token } : {});
}

/**
 * Delay de uma retentativa manual, sorteado na mesma janela que o socket.io
 * usaria. Uma rejeição de middleware faz o cliente marcar `skipReconnect`, de
 * modo que o backoff automático não roda; sem espalhar essa retentativa à mão a
 * frota voltaria toda junto — exatamente o que o jitter acima evita.
 */
/**
 * Uniform float in [0, 1) from the platform CSPRNG.
 *
 * `Math.random()` would do the job numerically, but it is flagged as
 * security-sensitive (SonarCloud S2245) and the reviewer cannot tell from the
 * call site whether the value feeds jitter or something that matters. Using
 * `crypto.getRandomValues` removes the question, costs nothing at this call
 * rate, and gives a better-distributed spread — which is the whole point of
 * jitter that has to break up a fleet-wide reconnect.
 */
function randomUnitInterval() {
  const buffer = new Uint32Array(1);
  globalThis.crypto.getRandomValues(buffer);
  return buffer[0] / 2 ** 32;
}

export function nextRetryDelayMs(options = SOCKET_IO_OPTIONS) {
  const { reconnectionDelay, randomizationFactor } = options;
  const deviation =
    randomUnitInterval() * randomizationFactor * reconnectionDelay;
  const signed = randomUnitInterval() < 0.5 ? -deviation : deviation;
  return Math.max(0, Math.round(reconnectionDelay + signed));
}

/**
 * `pingInterval` do servidor (apps/api/services/socketService.js). Enquanto a conexão vive chega um
 * ping a cada 25s, também numa aba de desktop em segundo plano: o navegador segura timers, não a rede.
 */
export const SERVER_PING_INTERVAL_MS = 25000;

/**
 * Sem ping por mais que isto, a página não estava recebendo: o celular apagou a tela ou o app foi
 * para segundo plano (a página congela), ou a rede caiu sem avisar. O que o servidor emitiu nesse
 * tempo pode ter se perdido.
 */
export const HEARTBEAT_STALE_MS = SERVER_PING_INTERVAL_MS + 5000;

/** Folga, ao voltar para a página, para o que ficou no buffer da conexão (pings inclusive) chegar. */
export const RESUME_SETTLE_MS = 1000;

/** A conexão passou mais de um intervalo de ping sem sinal de vida? */
export function isHeartbeatStale({ now, lastAliveAt }) {
  return Boolean(lastAliveAt) && now - lastAliveAt > HEARTBEAT_STALE_MS;
}

/**
 * Desde quando a conexão pode ter perdido eventos, no momento em que ela caiu.
 *
 * Com ping recente, desde a queda: uma piscada ou o corte de um deploy não ressincroniza nada (o
 * comportamento de sempre). Sem ping há mais de um intervalo, desde o último sinal de vida: no
 * celular a queda só é percebida quando a página acorda, e medir a partir daí dava "offline por 2s"
 * depois de minutos dormindo, sem ressincronizar. As mensagens que chegaram nesse tempo só
 * apareciam fechando e abrindo a plataforma.
 */
export function offlineSince({ now, lastAliveAt }) {
  return isHeartbeatStale({ now, lastAliveAt }) ? lastAliveAt : now;
}

export function SocketProvider({ children }) {
  const { isAuthenticated, user, setBillingStatus, setBillingPaymentLink, refreshFeatures } =
    useAuth();
  const queryClient = useQueryClient();
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const subscribedTickets = useRef(new Set());
  const reconnectAttempts = useRef(0);
  const disconnectedAt = useRef(null);

  useEffect(() => {
    console.log("[Socket] Auth State Changed:", {
      isAuthenticated,
      user: user?.email,
    });
    if (!isAuthenticated) {
      // Disconnect if logged out
      if (socket) {
        console.log("[Socket] Disconnecting due to logout");
        socket.disconnect();
        setSocket(null);
        setIsConnected(false);
        subscribedTickets.current.clear();
      }
      return;
    }

    // Create socket connection
    // WebSocket-only: Server is configured for websocket-only to work with PM2 cluster
    console.log("[Socket] Creating connection to:", SOCKET_URL);
    console.log(
      "[Socket] User:",
      user?.email,
      "Authenticated:",
      isAuthenticated,
    );
    const newSocket = io(SOCKET_URL, { ...SOCKET_IO_OPTIONS, auth: buildSocketAuth });

    // Último sinal de vida da conexão: o connect e cada ping do servidor
    let lastAliveAt = null;
    const markAlive = () => {
      lastAliveAt = Date.now();
    };
    newSocket.io.on("ping", markAlive);

    // Connection events
    newSocket.on("connect", () => {
      console.log("[Socket] Connected:", newSocket.id);
      const wasDisconnected = disconnectedAt.current !== null;
      markAlive();
      setIsConnected(true);
      reconnectAttempts.current = 0;

      // Re-subscribe to previously subscribed tickets
      subscribedTickets.current.forEach((ticketId) => {
        newSocket.emit("ticket:subscribe", ticketId);
      });

      // Auto-recovery: re-sync stale data after reconnection
      // Only if offline > 5s (micro-disconnects don't lose events)
      if (wasDisconnected) {
        const offlineMs = Date.now() - disconnectedAt.current;
        disconnectedAt.current = null;

        if (offlineMs > 5000) {
          console.log(`[Socket] Reconnected after ${Math.round(offlineMs / 1000)}s offline — syncing data`);
          // invalidateQueries only refetches ACTIVE (mounted) queries
          // Unmounted queries are just marked stale — no wasted requests
          queryClient.invalidateQueries({ queryKey: ["tickets"] });
          queryClient.invalidateQueries({ queryKey: ["messages"] });
          queryClient.invalidateQueries({ queryKey: ["inbox"] });
          queryClient.invalidateQueries({ queryKey: ["scheduled-messages"] });
          queryClient.invalidateQueries({ queryKey: ["scheduled-messages-counts"] });
          // Team chat (conversation list, open channel): its events were missed too
          queryClient.invalidateQueries({ queryKey: ["groups"] });
        }
      }
    });

    // Sessão substituída por um login mais novo (conta por licença): o servidor avisa e fecha. Revoga a sessão local e não
    // reconecta (o `io server disconnect` abaixo reconectaria, e o handshake seria recusado de novo).
    let sessionReplaced = false;
    newSocket.on("session:replaced", () => {
      sessionReplaced = true;
      announceSessionReplaced();
    });

    newSocket.on("disconnect", (reason) => {
      console.log("[Socket] Disconnected:", reason);
      setIsConnected(false);
      disconnectedAt.current = offlineSince({ now: Date.now(), lastAliveAt });

      // A server-initiated disconnect ("io server disconnect") does NOT trigger
      // socket.io's automatic reconnection, even with reconnection:true. This is
      // the blue-green cutover drain (io.disconnectSockets on the standby color):
      // without a manual reconnect the client stays dead — pinned to the retired
      // color — until a full page refresh, so realtime events silently stop.
      // Reconnect manually so nginx routes us to the active color and we re-join
      // the ticket rooms via the "connect" handler above.
      if (reason === "io server disconnect" && !sessionReplaced) {
        newSocket.connect();
      }
    });

    newSocket.on("connect_error", (error) => {
      console.error("[Socket] Connection error:", error.message);
      setIsConnected(false);
      reconnectAttempts.current++;

      // Reconexão com uma sessão que já foi substituída: nunca volta a tentar, só avisa.
      if (isSessionReplacedBody(error.data)) {
        sessionReplaced = true;
        announceSessionReplaced();
        newSocket.disconnect();
        return;
      }

      // O servidor recusa o handshake enquanto o pool não aquece e marca a
      // recusa como retryable. Uma rejeição de middleware faz o socket.io
      // marcar `skipReconnect`, então sem reconectar à mão o cliente ficaria
      // parado até um refresh. Espalha a volta pela mesma janela de jitter.
      if (error.data?.retryable) {
        setTimeout(() => newSocket.connect(), nextRetryDelayMs());
        return;
      }

      // If authentication error, don't keep trying
      if (
        error.message.includes("Authentication") ||
        error.message.includes("token")
      ) {
        console.log("[Socket] Auth error, stopping reconnection");
        newSocket.disconnect();
      }
    });

    newSocket.on("reconnect", (attemptNumber) => {
      console.log("[Socket] Reconnected after", attemptNumber, "attempts");
    });

    newSocket.on("reconnect_error", (error) => {
      console.error("[Socket] Reconnection error:", error.message);
    });

    newSocket.on("reconnect_failed", () => {
      console.error("[Socket] Reconnection failed after max attempts");
    });

    // Volta para a página (tela do celular acesa de novo, app de volta ao primeiro plano). Se ela
    // dormiu, a conexão que ainda se diz conectada pode estar morta: o servidor já a derrubou e o
    // cliente só perceberia no timeout de ping, até 85s depois, sem nada em tempo real nesse meio
    // tempo. Uma conexão nova entra nas salas de novo e o "connect" acima ressincroniza o que foi
    // perdido (o "disconnect" mede o tempo offline desde o último sinal de vida).
    let resumeTimer = null;
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      clearTimeout(resumeTimer);
      resumeTimer = setTimeout(() => {
        // Desconectada, a reconexão já está em curso e vai ressincronizar sozinha
        if (!newSocket.connected) return;
        if (!isHeartbeatStale({ now: Date.now(), lastAliveAt })) return;
        console.log("[Socket] Page resumed after missing heartbeats, reconnecting to sync");
        newSocket.disconnect();
        newSocket.connect();
      }, RESUME_SETTLE_MS);
    };
    const hasDocument = typeof document !== "undefined";
    if (hasDocument) {
      document.addEventListener("visibilitychange", handleVisibilityChange);
    }

    setSocket(newSocket);

    return () => {
      console.log("[Socket] Cleaning up connection");
      clearTimeout(resumeTimer);
      if (hasDocument) {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
      }
      newSocket.io.off("ping", markAlive);
      newSocket.disconnect();
    };
  }, [isAuthenticated, user?.accountId]);

  // ═══════════════════════════════════════════════════════════════
  // BILLING STATUS (real-time via webhook → socket)
  // ═══════════════════════════════════════════════════════════════

  useEffect(() => {
    if (!socket) return;

    const handleBillingChange = (data) => {
      console.log("[Socket] billing:status_changed", data);
      if (data.billingStatus) {
        setBillingStatus(data.billingStatus);
      }
      if (data.paymentLink !== undefined) {
        setBillingPaymentLink(data.paymentLink || null);
      }
      // Re-fetch full features (plan limits, trial state, etc.)
      refreshFeatures().catch(() => {});
    };

    socket.on("billing:status_changed", handleBillingChange);
    return () => socket.off("billing:status_changed", handleBillingChange);
  }, [socket, setBillingStatus, setBillingPaymentLink, refreshFeatures]);

  // ═══════════════════════════════════════════════════════════════
  // TICKET SUBSCRIPTION
  // ═══════════════════════════════════════════════════════════════

  /**
   * Subscribe to a ticket room for real-time updates
   */
  const subscribeToTicket = useCallback(
    (ticketId) => {
      if (!ticketId) return;

      if (socket?.connected) {
        socket.emit("ticket:subscribe", ticketId);
      }
      subscribedTickets.current.add(ticketId);
    },
    [socket],
  );

  /**
   * Unsubscribe from a ticket room
   */
  const unsubscribeFromTicket = useCallback(
    (ticketId) => {
      if (!ticketId) return;

      if (socket?.connected) {
        socket.emit("ticket:unsubscribe", ticketId);
      }
      subscribedTickets.current.delete(ticketId);
    },
    [socket],
  );

  // ═══════════════════════════════════════════════════════════════
  // TYPING INDICATORS
  // ═══════════════════════════════════════════════════════════════

  const typingTimeoutRef = useRef(null);

  /**
   * Send typing indicator
   */
  const sendTyping = useCallback(
    (ticketId, isTyping) => {
      if (!socket?.connected || !ticketId) return;

      if (isTyping) {
        socket.emit("typing:start", { ticketId });

        // Auto-stop typing after 3 seconds of no activity
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
        }
        typingTimeoutRef.current = setTimeout(() => {
          socket.emit("typing:stop", { ticketId });
        }, 3000);
      } else {
        socket.emit("typing:stop", { ticketId });
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
          typingTimeoutRef.current = null;
        }
      }
    },
    [socket],
  );

  // ═══════════════════════════════════════════════════════════════
  // EVENT LISTENERS
  // ═══════════════════════════════════════════════════════════════

  /**
   * Register an event listener
   */
  const on = useCallback(
    (event, callback) => {
      if (!socket) return () => {};

      socket.on(event, callback);
      return () => socket.off(event, callback);
    },
    [socket],
  );

  /**
   * Remove an event listener
   */
  const off = useCallback(
    (event, callback) => {
      if (!socket) return;
      socket.off(event, callback);
    },
    [socket],
  );

  // ═══════════════════════════════════════════════════════════════
  // CONTEXT VALUE
  // ═══════════════════════════════════════════════════════════════

  const value = {
    socket,
    isConnected,
    subscribeToTicket,
    unsubscribeFromTicket,
    sendTyping,
    on,
    off,
  };

  return (
    <SocketContext.Provider value={value}>{children}</SocketContext.Provider>
  );
}

export function useSocket() {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error("useSocket must be used within a SocketProvider");
  }
  return context;
}

export default SocketContext;
